import { useController, type FieldValues } from 'react-hook-form';
import { Form } from 'semantic-ui-react';

import {
  KeyValueEditor,
  type KeyValueEditorProps,
} from '@components/key-value-editor';

import { useFormEnterSubmit } from '../form-enter-context';

import { FieldError } from './field-error';
import type { BaseFieldProps } from './field.types';

export type FieldKeyValueProps<T extends FieldValues = FieldValues> =
  BaseFieldProps<T> & Omit<KeyValueEditorProps, 'onChange' | 'value'>;

/**
 * Key-value editor field wired to React Hook Form via `useController`.
 *
 * Wraps `KeyValueEditor` inside a `Form.Field` and handles error display
 * and `aria-errormessage` wiring automatically.
 *
 * When `outputFormat="tuples"` is set, the field emits `[string, string][]`
 * which preserves duplicate keys — enabling Zod schemas to detect and report
 * duplicates through the form's standard validation lifecycle.
 *
 * All additional props are forwarded to the underlying `KeyValueEditor`.
 *
 * @see KeyValueEditor
 */
export const FieldKeyValue = <T extends FieldValues>({
  className,
  control,
  label,
  name,
  required,
  width,
  ...rest
}: FieldKeyValueProps<T>) => {
  const onEnterSubmit = useFormEnterSubmit();
  const { field, fieldState } = useController({ control, name });
  const id = name;
  const errorId = `${id}-error`;
  const hasError = !!fieldState.error;

  return (
    <Form.Field className={className} required={required} width={width}>
      {label && <label htmlFor={id}>{label}</label>}

      {/* The RHF controller's onChange/value types are generic so TS cannot
          narrow the discriminated union. The cast is safe because the consumer
          picks the output format and RHF stores the matching shape. */}
      <KeyValueEditor
        {...({
          ...rest,
          onChange: field.onChange,
          ...(onEnterSubmit && { onEnterSubmit }),
          showDuplicateErrors: hasError,
          value: field.value ?? (rest.outputFormat === 'tuples' ? [] : {}),
        } as KeyValueEditorProps)}
      >
        <FieldError id={errorId} message={fieldState.error?.message} />
      </KeyValueEditor>
    </Form.Field>
  );
};
